// ============================================
// src/App/mascotPullMerge.js
// MA-1024 — the child's pal (students.mascot_id) follows MA-792's
// pending-sync rule (see autoReadPullMerge.js).
//
// THE BUG: the pull merge kept ANY local pal ("local wins":
// `prev.mascot || cloud`), so a device that already held a pal never adopted
// a newer pick made on another device. The push side sent
// `mascot_id: p.mascot?.id || null` on every syncToCloud call, so any future
// routine caller would have written a stale pal (or null) back.
//
// THE RULE (no new column): a pick sets `progress.mascotPendingSync` in the
// same write as `mascot`.
//   pending=true  -> the local pal is unsynced: keep it, (re)push it
//   pending=false -> the cloud's mascot_id wins (cross-device)
// A push whose returned row carries the same mascot_id clears the flag; a
// PGRST204 strip (column missing: updateStudent drops the key and the row
// comes back without it) leaves it set.
//
// KNOWN, ACCEPTED (RED r1 N-3): a students re-pull issued before a pick's
// PATCH lands, that also carries a changed assigned/suppressed stamp and
// resolves after the flag cleared, merges the pre-pick mascot_id (cloud wins)
// and reverts the local pal until the next re-pull. A stale row with only the
// old pal has the already-merged key (cloudMergeGuard.stampKey) and does not
// re-merge. Main's local-wins was immune; the window is tiny.
//
// Plain JS, no React, no DOM, no network: unit-tested in
// src/App/__tests__/ma1024PalPendingSync.test.mjs.
// ============================================

const isPalId = (v) => typeof v === "string" && v.length > 0;

/**
 * The pal slice of the cloud -> local merge.
 * @param {{ cloudMascotId: string|null|undefined, prevMascot: {id:string}|null|undefined, pending: boolean|null|undefined }} args
 * @returns {{ mascot: {id:string}|null|undefined, pending: boolean, needsPush: boolean }}
 */
export function resolveMascotPullMerge({ cloudMascotId, prevMascot, pending }) {
  const localId = prevMascot?.id;
  if (pending === true && isPalId(localId)) {
    // The cloud already has it: drop the flag, nothing to send.
    if (cloudMascotId === localId) return { mascot: prevMascot, pending: false, needsPush: false };
    return { mascot: prevMascot, pending: true, needsPush: true };
  }
  // Not pending: the cloud wins. An absent column (undefined) or a cloud row
  // with no pal yet (null) leaves the local pal alone (never { id: null }).
  if (isPalId(cloudMascotId)) {
    return { mascot: localId === cloudMascotId ? prevMascot : { id: cloudMascotId }, pending: false, needsPush: false };
  }
  return { mascot: prevMascot, pending: false, needsPush: false };
}

/**
 * @returns {{ patch: { mascot: any, mascotPendingSync: boolean },
 *             push: { studentId: string, localChildId: string|null, mascotId: string } | null }}
 */
export function planMascotOnMerge({ prev, cloudStudent, localChildId }) {
  const m = resolveMascotPullMerge({
    cloudMascotId: cloudStudent?.mascot_id,
    prevMascot: prev?.mascot,
    pending: prev?.mascotPendingSync,
  });
  return {
    patch: { mascot: m.mascot, mascotPendingSync: m.pending },
    push: m.needsPush ? { studentId: cloudStudent.id, localChildId: localChildId ?? null, mascotId: m.mascot.id } : null,
  };
}

/**
 * syncToCloud's students payload: `mascot_id` only while the local pal is a
 * pick the cloud has not confirmed. A progress record with a pal and no
 * pending flag yields NO `mascot_id` key, so a routine caller can never write
 * a stale pal (or null) over another device's newer pick.
 */
export function buildStudentSyncPayload(p) {
  if (p?.mascotPendingSync === true && isPalId(p?.mascot?.id)) return { mascot_id: p.mascot.id };
  return {};
}

/** The id the cloud confirmed for this payload, or null (no pal sent, a
 *  failed write, or a PGRST204 strip whose row has no mascot_id). */
export function confirmedMascotId(payload, row) {
  const sent = payload?.mascot_id;
  if (!isPalId(sent) || !row || row.mascot_id !== sent) return null;
  return sent;
}

/** A pick: the pal and its pending flag in ONE write. */
export function withMascotPick(prev, id) {
  return { ...prev, mascot: { id }, mascotPendingSync: true };
}

/** Clear the flag only if the stored pal is still the one the cloud confirmed
 *  (a newer pick made while the write was in flight stays pending). */
export const clearMascotPendingIfCurrent = (confirmedId) => (prev) =>
  prev && prev.mascotPendingSync === true && prev.mascot?.id === confirmedId
    ? { ...prev, mascotPendingSync: false }
    : prev;

// ── MA-1024 RED r1 M-1: the onboarding pal step is a pick too ───────────
/** Spread into handleOnboardingComplete's new record (after `...data`): the
 *  pal picked at /onboarding/kid/pal is a local pick the cloud has never
 *  received (createStudent sends no mascot_id, and a reused same-name row
 *  holds an older pal), so it is pending. The first merge for the new child
 *  then keeps it and re-pushes it (or clears the flag when the cloud already
 *  holds it). */
export function onboardingMascotPending(data) {
  return isPalId(data?.mascot?.id) ? { mascotPendingSync: true } : {};
}

// ── MA-1024 RED r1 S-1: the App.jsx wiring, as child-scoped helpers ─────
/**
 * The students leg of syncToCloud. Sends the builder's payload (nothing when
 * it is empty) and names the pal the RETURNED row confirmed: null after a
 * PGRST204 strip (updateStudent drops the key and the row has no mascot_id).
 * Throws what updateStudentFn throws.
 */
export async function syncStudentRow({ studentId, progress, updateStudentFn }) {
  const payload = buildStudentSyncPayload(progress);
  if (Object.keys(payload).length === 0) return { mascotConfirmed: null };
  const row = await updateStudentFn(studentId, payload);
  return { mascotConfirmed: confirmedMascotId(payload, row) };
}

/**
 * A pick's cloud write. `childId` is the child that picked, captured by the
 * caller BEFORE this call; the flag is cleared for that child only (id-scoped
 * setter), and only while its stored pal is still the confirmed one.
 * Resolves with sync's result (null/undefined when nothing was written).
 */
export async function confirmPickSync({ sync, progress, childId, setProgressForChild }) {
  const r = await sync(progress);
  if (r?.mascotConfirmed && childId) setProgressForChild(childId, clearMascotPendingIfCurrent(r.mascotConfirmed));
  return r;
}

/**
 * The merge's re-push of a pending pal (planMascotOnMerge's `push`). A row
 * carrying the same mascot_id clears the flag for push.localChildId (bound at
 * merge time); a strip, a failure or a different row leaves it set, so the
 * next load retries. NOTE: with the column permanently missing every load
 * costs a PATCH + a stripped retry (main paid that per pick only); 0041 is
 * applied in production.
 */
export async function pushPendingMascot({ push, updateStudentFn, setProgressForChild, onError }) {
  if (!push || !push.studentId || !isPalId(push.mascotId)) return { ok: true, confirmed: null };
  const payload = { mascot_id: push.mascotId };
  try {
    const row = await updateStudentFn(push.studentId, payload);
    const confirmed = confirmedMascotId(payload, row);
    if (confirmed && push.localChildId) setProgressForChild(push.localChildId, clearMascotPendingIfCurrent(confirmed));
    return { ok: true, confirmed };
  } catch (e) {
    if (onError) onError(e);
    return { ok: false, confirmed: null };
  }
}

/**
 * MascotPicker's confirm (the /pal swap): the pal and its pending flag in one
 * value-form write through the app api, then the cloud write (fire and
 * forget: the api's syncToCloud clears the flag on a confirming row).
 */
export function commitPalPick(app, slug) {
  const updated = withMascotPick(app.progress ?? {}, slug);
  app.setProgress(updated);
  if (app.syncToCloud) {
    void Promise.resolve(app.syncToCloud(updated)).catch(() => {
      // Non-blocking: the local pick is stored and stays pending.
    });
  }
  return updated;
}
